import React, {useEffect, useState} from 'react';
import axios from 'axios';
import { Table, Input, InputGroup, Button, DatePicker } from "rsuite";

const { Column, HeaderCell, Cell } = Table;

const styles = {
  width: 150,
  marginLeft: "5px",
  marginRight: "5px"
};

const CACHE_KEY = "crypto_price_cache";
const CACHE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

const HISTORICAL_CACHE_KEY = "historical_price_cache";
const HISTORICAL_CACHE_DURATION = 24 * 60 * 60 * 1000; // optional: 24h expiration

const FomoCrypto = () => {
    const [investmentAmount, setInvestmentAmount] = useState("100");
    const [investmentDate, setInvestmentDate] = useState(new Date("2025-01-01"));
    const [cryptoData, setCryptoData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [topCryptos, setTopCryptos] = useState([]);
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    useEffect(() => {
        const CACHE_KEY = "top_cryptos_cache";
        const CACHE_DURATION = 20 * 60 * 1000; // 10 minutes

        const fetchTopCryptos = async () => {
            const cached = localStorage.getItem(CACHE_KEY);

            if (cached) {
                const parsed = JSON.parse(cached);
                const now = Date.now();

                if (now - parsed.timestamp < CACHE_DURATION) {
                    setTopCryptos(parsed.data);
                    return;
                }
            }

            try {
                const response = await axios.get("https://data-api.cryptocompare.com/asset/v1/search", {
                    params: {
                        search_string: "",
                        limit: 10,
                        response_format: "JSON"
                    }
                });

                if (response.data?.Data?.LIST) {
                    const topCryptos = response.data.Data.LIST;
                    setTopCryptos(topCryptos);

                    localStorage.setItem(
                        CACHE_KEY,
                        JSON.stringify({
                            timestamp: Date.now(),
                            data: topCryptos
                        })
                    );
                }
            } catch (error) {
                console.error("Failed to fetch top cryptos", error);
            }
        };

        fetchTopCryptos();
    }, []);

    const handleFetchData = async () => {
        if (!investmentAmount || !investmentDate) {
            alert("Please provide both investment amount and date!");
            return;
        }

        setLoading(true);

        const toTimestamp = Math.floor(new Date(investmentDate).setHours(23, 59, 59) / 1000);

        try {
            const formattedData = await Promise.all(
                topCryptos.map(async (crypto) => {
                    const priceData = await getCachedHistoricalPrice(crypto.SYMBOL, toTimestamp);

                    if (priceData) {
                        const { close } = priceData;

                        const investedCryptoAmount = investmentAmount / close;
                        const currentPrice = await getCachedCurrentPrice(crypto.SYMBOL);
                        const currentValue = investedCryptoAmount * currentPrice;
                        const percentageChange = ((currentValue - investmentAmount) / investmentAmount) * 100;

                        return {
                            name: crypto.NAME,
                            image: crypto.LOGO_URL || "",
                            date: new Date(priceData.time * 1000).toLocaleDateString(),
                            close: formatNumber(close),
                            investedCrypto: `${crypto.SYMBOL} ${investedCryptoAmount.toFixed(6)}`,
                            currentPrice: formatNumber(currentPrice),
                            currentValue: formatNumber(currentValue),
                            percentageChange: percentageChange.toFixed(2) + "%",
                            symbol: crypto.SYMBOL
                        };
                    } else {
                        return { name: crypto.NAME, error: "No data available for the selected date" };
                    }
                })
            );

            setCryptoData(formattedData);
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Failed to fetch data. Please try again later.");
        }

        setLoading(false);
    };

    const formatNumber = (number) =>
        parseFloat(number).toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) + " €";

    let priceCacheWriteLock = Promise.resolve();

    const getCachedCurrentPrice = async (symbol) => {
        const now = Date.now();

        let cache = {};
        const rawCache = localStorage.getItem(CACHE_KEY);

        if (rawCache) {
            try {
                cache = JSON.parse(rawCache);
            } catch (err) {
                console.warn("Failed to parse cache, resetting...", err);
                cache = {};
            }
        }

        const cachedEntry = cache[symbol];
        if (cachedEntry && now - cachedEntry.timestamp < CACHE_EXPIRY_MS) {
            // console.log(`Using cached price for ${symbol}:`, cachedEntry);
            return cachedEntry.price;
        }

        // Delay when fetching new price
        await delay(500);

        try {
            const response = await axios.get("https://min-api.cryptocompare.com/data/price", {
                params: {
                    fsym: symbol,
                    tsyms: "EUR",
                },
            });

            const price = response.data?.EUR || 0;

            // Queue this write to prevent race conditions
            priceCacheWriteLock = priceCacheWriteLock.then(() => {
                const updatedRaw = localStorage.getItem(CACHE_KEY);
                let currentCache = {};

                try {
                    currentCache = updatedRaw ? JSON.parse(updatedRaw) : {};
                } catch (e) {
                    console.warn("Corrupted cache during update, resetting.");
                    currentCache = {};
                }

                const updatedCache = {
                    ...currentCache,
                    [symbol]: {
                        price,
                        timestamp: Date.now(),
                    }
                };

                localStorage.setItem(CACHE_KEY, JSON.stringify(updatedCache));
                // console.log(`Saved ${symbol} price to cache.`, updatedCache);
            });

            // Wait for this write to finish before returning
            await priceCacheWriteLock;

            return price;
        } catch (e) {
            console.error(`Failed to fetch price for ${symbol}`, e);
            return 0;
        }
    };

    let historicalCacheWriteLock = Promise.resolve();

    const getCachedHistoricalPrice = async (symbol, toTimestamp) => {
        const cacheKey = `${symbol}_${toTimestamp}`;
        const now = Date.now();

        let cache = {};
        const cacheRaw = localStorage.getItem(HISTORICAL_CACHE_KEY);

        if (cacheRaw) {
            try {
                cache = JSON.parse(cacheRaw);
            } catch (err) {
                console.warn("Failed to parse historical cache, resetting...", err);
                cache = {};
            }
        }

        if (cache[cacheKey] && (now - cache[cacheKey].timestamp < HISTORICAL_CACHE_DURATION)) {
            // console.log(`Using cached historical price for ${symbol} at ${toTimestamp}`);
            return cache[cacheKey].data;
        }

        // Delay only when fetching new data
        await delay(500);

        try {
            const response = await axios.get("https://min-api.cryptocompare.com/data/v2/histoday", {
                params: {
                    fsym: symbol,
                    tsym: "EUR",
                    limit: 1,
                    toTs: toTimestamp,
                    aggregate: 1,
                },
            });

            const priceData = response.data?.Data?.Data || [];

            const matchingPriceData = priceData.find(
                (priceData) =>
                    priceData.time >= toTimestamp - 86400 &&
                    priceData.time < toTimestamp
            );

            if (matchingPriceData) {
                // Queue the write to localStorage to avoid race conditions
                historicalCacheWriteLock = historicalCacheWriteLock.then(() => {
                    let latestCache = {};
                    const latestRaw = localStorage.getItem(HISTORICAL_CACHE_KEY);

                    try {
                        latestCache = latestRaw ? JSON.parse(latestRaw) : {};
                    } catch {
                        latestCache = {};
                    }

                    const updatedCache = {
                        ...latestCache,
                        [cacheKey]: {
                            data: matchingPriceData,
                            timestamp: Date.now()
                        }
                    };

                    localStorage.setItem(HISTORICAL_CACHE_KEY, JSON.stringify(updatedCache));
                    // console.log(`Saved historical price for ${symbol} to cache:`, updatedCache);
                });

                await historicalCacheWriteLock;

                return matchingPriceData;
            }

            console.warn(`No matching historical data found for ${symbol} at ${toTimestamp}`);
            return null;

        } catch (error) {
            console.error(`Failed to fetch historical price for ${symbol}`, error);
            return null;
        }
    };

    return (
        <div className="fomo-crypto-container">
            <div style={{display: "flex", alignItems: "baseline", justifyContent: "center"}}>
                If I invested
                <InputGroup size={"sm"} inside style={styles}>
                    <Input placeholder="100"
                        type="number"
                        value={investmentAmount}
                        onChange={(value) => setInvestmentAmount(value)}
                        style={{ marginRight: "10px" }}
                    />
                    <InputGroup.Addon>€</InputGroup.Addon>
                </InputGroup>

                into one cryptocurrency on
                <DatePicker
                    format="yyyy-MM-dd"
                    placeholder="Date"
                    size={"sm"}
                    value={investmentDate}
                    onChange={(date) => setInvestmentDate(date)}
                    style={styles}
                />
                , today it would be worth...
            </div>
            <br/>
                <Button appearance="primary" onClick={handleFetchData} loading={loading}>
                    Calculate
                </Button>

            <Table
                loading={loading}
                height={400}
                data={cryptoData}
                wordWrap
                autoHeight
                cellBordered
                virtualized
                headerHeight={30}
                rowHeight={30}
                bordered={true}
            >
                <Column width={200}>
                    <HeaderCell>Crypto</HeaderCell>
                    <Cell dataKey="name" />
                </Column>

                <Column width={150}>
                    <HeaderCell>Old Price (€)</HeaderCell>
                    <Cell dataKey="close" />
                </Column>

                <Column width={200}>
                    <HeaderCell>Current Price (€)</HeaderCell>
                    <Cell dataKey="currentPrice" />
                </Column>

                <Column width={200}>
                    <HeaderCell>Invested Crypto</HeaderCell>
                    <Cell dataKey="investedCrypto" />
                </Column>



                <Column width={200}>
                    <HeaderCell>Value now (€)</HeaderCell>
                    <Cell dataKey="currentValue" />
                </Column>

                <Column width={200}>
                    <HeaderCell>% Change (FOMO)</HeaderCell>
                    <Cell dataKey="percentageChange" />
                </Column>
            </Table>

            <div className={"fomo-results"}>
                {cryptoData.map((data, index) => (
                  <div className={"fomo-row"} key={index}>
                        <div className={"image"}><img src={data.image} width={25} height={25}  alt={data.name}/></div>
                        <div className={"name"}>
                            <div style={{paddingBottom: "5px"}}><b>{data.name}</b></div>
                            <div>{data.investedCrypto || "N/A"}</div>
                        </div>
                        <div className={"price"}><b>{data.currentValue || "N/A"}</b></div>
                        <div className={"percentage"}>{data.percentageChange || "N/A"}</div>
                  </div>
                ))}
              </div>
        </div>
    );
};

export default FomoCrypto;